import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Typography from "@mui/material/Typography";
import "./App.css";

import { useAppDispatch, useAppSelector } from "./store";
import {
  addAnswer,
  BASE_URL,
  endQuiz,
  fetchAllData,
  WEBHOOK_URL,
} from "./store/slice/quiz";
import { Answer, Question } from "./types";
import { Form, SubmissionForm } from "./components/SubmissionForm";
import { QuestionItem } from "./components/QuestionItem";
import { IntroductionContent } from "./components/IntroductionContent";
import { ScoreContent } from "./components/ScoreContent";
import axios from "axios";

export default function App() {
  const [activeStep, setActiveStep] = React.useState(0);
  const questions = useAppSelector((s) => s.quiz.questions);
  const generalContent = useAppSelector((s) => s.quiz.generalContent);
  const answers = useAppSelector((s) => s.quiz.answers);
  const ended = useAppSelector((s) => s.quiz.ended);
  const fetchState = useAppSelector((s) => s.quiz.requestStatus);
  const [currentAnswer, setCurrentAnswer] = React.useState<Answer | null>(null);
  const dispatch = useAppDispatch();
  const [postResponse, setPostResponse] =
    React.useState<{ score: number; text: string }>();
  React.useEffect(() => {
    dispatch(fetchAllData());
  }, [dispatch]);

  const getShowRemarks = React.useCallback(
    (currentStep, question: Question) => {
      if (
        currentStep === activeStep &&
        currentAnswer &&
        currentAnswer[question.Id] !== question.Correct_Answer
      )
        return true;
      return false;
    },
    [activeStep, currentAnswer]
  );

  const handleNext = () => {
    setActiveStep(activeStep + 1);
    setCurrentAnswer(answers[activeStep + 1]);
  };

  const handleFinish = () => {
    dispatch(endQuiz());
  };

  const handleNextButtonClick = () => {
    if (!currentAnswer) return;
    dispatch(addAnswer({ [activeStep]: currentAnswer }));
    handleNext();
  };

  const handleBackButtonClick = () => {
    const answer = answers[activeStep - 1];
    setCurrentAnswer(answer);

    setActiveStep(activeStep - 1);
  };

  const handleAnswerSelect = ({
    answer,
    correctAnswer,
    questionId,
  }: {
    answer: string;
    correctAnswer: string;
    questionId: string;
  }) => {
    if (answer === correctAnswer) {
      handleNext();
      dispatch(
        addAnswer({
          [activeStep]: {
            [questionId]: answer,
          },
        })
      );
    } else {
      setCurrentAnswer({ [questionId]: answer });
    }
  };

  const postData = async (formData: Form) => {
    try {
      const request1 = await axios.post(
        BASE_URL,
        {
          answers: Object.values(answers).reduce((acc, curr) => {
            return {
              ...acc,
              ...curr,
            };
          }, {} as Answer),
          formData,
        },
        {
          headers: {
            "Content-Type": "text/plain",
          },
        }
      );
      setPostResponse(request1.data);

      await axios.post(
        WEBHOOK_URL,
        new URLSearchParams({
          ...Object.values(answers).reduce((acc, curr) => {
            return {
              ...acc,
              ...curr,
            };
          }, {} as Answer),
          ...formData,
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
    } catch (e) {
      console.log(e);
    }
  };

  const onSubmit = (data: Form) => {
    postData(data);
    handleNext();
  };

  if (fetchState.isError) return <div>{fetchState.error}</div>;
  if (fetchState.isLoading) return <div></div>;

  return (
    <div
      style={{
        backgroundImage: `url(${generalContent?.["Background Image"].value})`,
        backgroundColor: `${generalContent?.["Background Color"].value}`,
        minHeight: "100vh",
      }}
      className="Background"
    >
      <Box sx={{ marginLeft: "auto", marginRight: "auto", maxWidth: "57rem" }}>
        {generalContent && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={generalContent.Logo.value}
              alt="hello"
              style={{ marginTop: "3rem", maxWidth: "16rem" }}
            />
            <Typography
              sx={{
                color: "white",
                fontSize: "2.8rem",
                marginTop: "2rem",
              }}
            >
              {generalContent["Heading Text"].value}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: "rgba(250, 250, 250, 0.8)",
            padding: "3rem",
            marginTop: "2.2rem",
            borderRadius: "0.3rem",
          }}
        >
          {ended && (
            <Typography>
              An email with your results will be sent to you shortly.
            </Typography>
          )}
          {!ended && (
            <Stepper activeStep={activeStep} orientation="vertical">
              <Step>
                <StepLabel>Introduction</StepLabel>
                <StepContent>
                  {generalContent && (
                    <IntroductionContent
                      handleNext={handleNext}
                      introductionValue={generalContent.Introduction.value}
                    />
                  )}
                </StepContent>
              </Step>
              {questions.map((question, index) => (
                <Step key={question.Id}>
                  <StepLabel>{question.Title}</StepLabel>
                  <StepContent>
                    <QuestionItem
                      showRemarks={getShowRemarks(index + 1, question)}
                      handleAnswerSelect={handleAnswerSelect}
                      handleNextButtonClick={handleNextButtonClick}
                      handleBackButtonClick={handleBackButtonClick}
                      question={question}
                      currentAnswer={currentAnswer}
                    />
                  </StepContent>
                </Step>
              ))}
              <Step>
                <StepLabel>
                  To Receive Your Final Score Please Fill Out The Below
                </StepLabel>
                <StepContent>
                  <SubmissionForm handleOnSubmit={onSubmit} />
                </StepContent>
              </Step>
              <Step>
                <StepLabel>Your Score</StepLabel>
                <StepContent>
                  <ScoreContent
                    handleNext={handleFinish}
                    score={postResponse?.score}
                    text={postResponse?.text}
                  />
                </StepContent>
              </Step>
            </Stepper>
          )}
        </Box>
      </Box>
      <Typography
        variant="subtitle2"
        color="white"
        textAlign="center"
        paddingY={5}
        component="div"
      >
        ©{" "}
        <a style={{ color: "white" }} href="https://www.infotrust.com.au/">
          InfoTrust
        </a>{" "}
        {new Date().getFullYear()} - Developed by{" "}
        <a style={{ color: "white" }} href="https://www.ITfiers.com/">
          ITfiers
        </a>
      </Typography>
    </div>
  );
}
